<template>
  <dynamic-yield-template
    v-for="target of modalTargets"
    :key="target"
    :target="target"
  />
</template>

<script>

/**
 * Vue: Dynamic yield (dynamic-yield)
 * -----------------------------------------------------------------------------
 * Dynamic Yield manager.
 * @param {Array} modalTargets - The IDs for the DY modal target HTML elements
 *
 */
import { mapActions } from 'vuex'
import { scrollDepth as DYScrollDepthEvent } from '~/helpers/dynamic-yield'
import DynamicYieldTemplate from '~async/dynamic-yield-template/dynamic-yield-template'

export default {
  name: 'DynamicYield',

  components: {
    DynamicYieldTemplate,
  },

  props: {
    modalTargets: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      scrollDepth: 0,
    }
  },

  watch: {

    /**
     * Track scroll depth
     * @param {Number} newScrollDepth - New scroll depth
     */
    scrollDepth(newScrollDepth) {
      DYScrollDepthEvent(newScrollDepth)
    },
  },

  /**
   * Grabs campaign data for all configured target elements and
   * sets the campaigns in the Vuex store.
   */
  mounted() {
    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setCampaign: 'dynamic-yield/setCampaign',
      setLoaded: 'dynamic-yield/setLoaded',
    }),

    /**
     * Grabs campaign data for the requested experience, by looking for its
     * child elements with [data-variable].
     * @param {HTMLElement} experienceElement - The experience element
     * @returns {Object} Campaign data
     */
    getExperienceData(experienceElement) {
      return new Promise((resolve) => {
        window.DY?.API('callback', () => {
          const data = {}

          experienceElement.querySelectorAll('[data-variable]')?.forEach((variable) => {
            const key = variable.dataset.variable || null
            const value = variable.innerText.trim() || null

            if (!key) {
              return
            }

            data[key] = value
          })

          if (!Object.keys(data).length) {
            resolve(null)
          }

          resolve({
            data,
          })
        })
      })
    },

    /**
     * Sets event listeners
     */
    setEventListeners() {
      // Watches for DY loaded events from campaigns
      window.cnvs?.EventBus?.listen(`dy:loaded`, async(experienceId) => {
        const experienceIdElement = document.querySelector(`[data-dy-experience-id="${experienceId}"]`)

        if (!experienceIdElement) {
          return
        }

        const target = experienceIdElement.closest('[data-dy-embedded-object]')?.id
        // TODO: Try and pass the data through this event's payload
        const data = await this.getExperienceData(experienceIdElement, target)

        if (data) {
          this.setCampaign({
            ...data,
            target,
          })
          this.setLoaded(target)
          return
        }

        this.setLoaded(target)
      })

      // Watches page scroll to track depth
      document.addEventListener('scrollend', () => {
        const bottom = document.documentElement.scrollHeight - window.innerHeight
        const current = window.scrollY
        const percentage = 100 - ((bottom - current) / bottom * 100)
        let scrollDepth = 100

        if (percentage < 10) {
          scrollDepth = 0
        } else if (percentage < 25) {
          scrollDepth = 10
        } else if (percentage < 50) {
          scrollDepth = 25
        } else if (percentage < 75) {
          scrollDepth = 50
        } else if (percentage < 100) {
          scrollDepth = 75
        }

        if (this.scrollDepth === scrollDepth) {
          return
        }

        this.scrollDepth = scrollDepth
      })
    },
  },
}
</script>
